// シミュレーション計算用のユーティリティ関数を拡張
import { runMultiAssetSimulation } from './simulation';
import { lifePlanSchema } from './validation';
import { z } from 'zod';

/**
 * ライフプランデータからリスクシミュレーション用のパラメータを抽出する
 * @param formData ライフプランデータ
 * @returns リスクシミュレーション用パラメータ
 */
export function getLifePlanSimulationParams(formData: z.infer<typeof lifePlanSchema>) {
  const yearlyData = calculateYearlyData(formData);

  // 初期投資額（現在の資産総額）
  const initialInvestment = Object.values(formData.assets).reduce(
    (sum, asset) => sum + asset.amount, 0
  );

  // 年ごとの月額投資額（貯蓄額 / 12）
  // RiskSimulationは「投資」のみを計算するため、Savings（収入-支出）を投資に回すと仮定する
  const investmentSchedule = yearlyData.map(data => Math.max(0, Math.floor(data.savings / 12)));

  // 投資期間
  const investmentPeriod = formData.simulationSettings.simulationPeriod;

  // 現在の設定から期待リターンとボラティリティの加重平均を計算するのも良いが、
  // ここではRiskSimulation側で指定させるか、あるいは加重平均を計算して返すか。
  // RiskSimulation側で調整したい要望が強いと予想されるため、初期値として計算して返す。

  const assets = Object.values(formData.assets);
  let totalWeightedReturn = 0;
  let totalWeightedVolatility = 0;
  let totalAmount = 0;

  assets.forEach(asset => {
    if (asset.amount > 0) {
      totalWeightedReturn += asset.amount * (asset.return || 0);
      totalWeightedVolatility += asset.amount * (asset.volatility || 0);
      totalAmount += asset.amount;
    }
  });

  // 資産がゼロの場合はデフォルト値を返す
  const returnRate = totalAmount > 0 ? totalWeightedReturn / totalAmount : 5; // Default 5%
  const volatility = totalAmount > 0 ? totalWeightedVolatility / totalAmount : 15; // Default 15%

  return {
    initialInvestment,
    investmentSchedule,
    investmentPeriod,
    returnRate,
    volatility
  };
}


export function calculateMonthlySummary(formData: z.infer<typeof lifePlanSchema>) {
  // 月収の計算（年収 + ボーナスを12で割る）
  const monthlyIncome = (formData.income.annualIncome + formData.income.annualBonus) / 12

  // 月間支出の計算（すべての支出項目の合計）
  const monthlyExpense = formData.expense ? Object.values(formData.expense as Record<string, number>).reduce(
    (sum: number, value: number) => sum + value, 0
  ) : 0

  // 月間投資の計算（すべての投資項目の合計）
  const monthlyInvestment = formData.investment ? Object.values(formData.investment as Record<string, number>).reduce(
    (sum: number, value: number) => sum + value, 0
  ) : 0

  // 月間貯蓄の計算（収入 - 支出）
  const monthlySavings = monthlyIncome - monthlyExpense - monthlyInvestment

  // 総資産の計算
  const totalAssets = formData.assets ? Object.values(formData.assets).reduce(
    (sum: number, asset: { amount: number; return: number; volatility: number }) =>
      sum + asset.amount, 0
  ) : 0

  // 総負債の計算
  const totalLiabilities = formData.liabilities ? Object.values(formData.liabilities).reduce(
    (sum: number, loans: { name: string; amount: number; period: number; interestRate: number; }[]) =>
      sum + loans.reduce((loanSum, loan) => loanSum + (loan?.amount || 0), 0), 0) : 0

  // 純資産の計算（総資産 - 総負債）
  const netWorth = totalAssets - totalLiabilities

  return {
    monthlyIncome,
    monthlyExpense,
    monthlySavings,
    monthlyInvestment,
    totalAssets,
    totalLiabilities,
    netWorth,
  }
}

// 共通の基本計算を行う関数
function calculateBaseParameters(formData: z.infer<typeof lifePlanSchema>) {
  const currentAge = formData.basicInfo.age;
  const simulationPeriod = formData.simulationSettings.simulationPeriod;
  const inflationRate = formData.simulationSettings.inflationRate / 100;
  const salaryIncreaseRate = formData.income.salaryIncreaseRate / 100;
  const retirementAge = formData.lifeGoals.retirementAge;

  // 配偶者と子どもの年齢を取得
  const hasSpouse = formData.basicInfo.familyStructure.hasSpouse;
  const spouseAge = formData.basicInfo.familyStructure.spouseAge || 0;
  const children = formData.basicInfo.familyStructure.children || [];

  // 初期値の設定
  const initialIncome = formData.income.annualIncome + formData.income.annualBonus;
  const initialExpense = Object.values(formData.expense as Record<string, number>)
    .reduce((sum: number, value: number) => sum + value, 0) * 12;
  const retirementExpense = formData.lifeGoals.retirementMonthlyExpense * 12;

  return {
    currentAge,
    simulationPeriod,
    inflationRate,
    salaryIncreaseRate,
    retirementAge,
    initialIncome,
    initialExpense,
    retirementExpense,
    hasSpouse,
    spouseAge,
    children
  };
}

// ライフイベントの処理を行う関数
function processLifeEvents(
  formData: z.infer<typeof lifePlanSchema>,
  age: number,
  currentAge: number
) {
  const events = [];
  let eventExpenses = 0;

  // 退職イベント
  if (age === formData.lifeGoals.retirementAge) {
    events.push({
      age,
      type: "retirement",
      name: "lifePlan.result.timeline.retirement",
      amount: 0,
    });
  }

  // 住宅購入イベント
  if (formData.lifeGoals.housingPurchase.planned &&
    formData.lifeGoals.housingPurchase.year === age) {
    events.push({
      age,
      type: "housing",
      name: "lifePlan.result.timeline.housingPurchase",
      amount: formData.lifeGoals.housingPurchase.amount,
    });
    eventExpenses += formData.lifeGoals.housingPurchase.amount;
  }

  // 教育資金イベント
  formData.lifeGoals.educationFunds.forEach((fund: any) => {
    formData.basicInfo.familyStructure.children.forEach((child: any) => {
      const eventAge = currentAge + fund.age - child.age;
      if (eventAge === age) {
        events.push({
          age,
          type: "education",
          name: "lifePlan.result.timeline.education",
          amount: fund.amount,
          params: {
            childName: child.name || "lifePlan.result.timeline.child",
            fundName: fund.name || "lifePlan.result.timeline.educationFund"
          }
        });
        eventExpenses += fund.amount;
      }
    });
  });

  return { events, eventExpenses };
}

// 負債の総額を計算する関数
function calculateTotalLiabilities(formData: z.infer<typeof lifePlanSchema>, age: number, year: number): number {
  // すべての負債（住宅ローンを含む）を計算
  const totalLiabilities = Object.values(formData.liabilities).reduce(
    (sum: number, loans: { name: string; amount: number; period: number; interestRate: number; }[]) =>
      sum + loans.reduce((loanSum, loan) => {
        if (loan.amount && loan.period) {
          // 初期状態からの経過年数を計算
          const yearsPassed = year;
          const remainingAmount = yearsPassed < loan.period
            ? calculateRemainingLoanAmount(loan.amount, loan.period, loan.interestRate / 100, yearsPassed)
            : 0;
          return loanSum + remainingAmount;
        }
        return loanSum;
      }, 0), 0
  );

  return totalLiabilities;
}

// 元利均等返済方式での残高計算
function calculateRemainingLoanAmount(principal: number, period: number, interestRate: number, yearsPassed: number): number {
  if (yearsPassed <= 0) return principal;
  if (yearsPassed >= period) return 0;

  // 金利が0の場合は単純に元金を期間で割る
  if (interestRate === 0) {
    const monthlyPayment = principal / (period * 12);
    return principal - (monthlyPayment * yearsPassed * 12);
  }

  // 月次金利と支払い回数
  const monthlyRate = interestRate / 12;
  const totalPayments = period * 12;
  const paymentsMade = yearsPassed * 12;

  // 月々の支払い額を計算
  const monthlyPayment = (principal * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) /
    (Math.pow(1 + monthlyRate, totalPayments) - 1);

  // 残高を計算
  const remainingBalance = principal * Math.pow(1 + monthlyRate, paymentsMade) -
    (monthlyPayment * (Math.pow(1 + monthlyRate, paymentsMade) - 1) / monthlyRate);

  return Math.max(0, remainingBalance);
}

// メインのシミュレーション関数
function runSimulation(
  formData: z.infer<typeof lifePlanSchema>,
  simulationType: 'simple' | 'monteCarlo'
) {
  const baseParams = calculateBaseParameters(formData);
  const yearlyData = [];

  // 資産のディープコピーを作成
  const initialAssets = {
    savings: { ...formData.assets.savings },
    stocks: { ...formData.assets.stocks },
    bonds: { ...formData.assets.bonds },
    mutualFunds: { ...formData.assets.mutualFunds },
    insurance: { ...formData.assets.insurance },
    other: { ...formData.assets.other }
  };

  let currentAssets = initialAssets;
  let currentIncome = baseParams.initialIncome;
  let currentExpense = baseParams.initialExpense;

  for (let year = 0; year < baseParams.simulationPeriod; year++) {
    const age = baseParams.currentAge + year;

    // 家族の年齢を計算
    const familyAges = {
      spouseAge: baseParams.hasSpouse ? baseParams.spouseAge + year : 0,
      childrenAges: baseParams.children.map((child: any) => child.age + year)
    }

    // 収入計算
    const yearlyIncome = age > baseParams.retirementAge ? 0 : currentIncome;

    // 支出計算
    const liabilityPayments = calculateLiabilityPayments(formData, age, year);
    const { events, eventExpenses } = processLifeEvents(formData, age, baseParams.currentAge);

    const yearlyExpense = (age > baseParams.retirementAge ? baseParams.retirementExpense : currentExpense)
      + liabilityPayments + eventExpenses;

    // 貯蓄計算
    const yearlySavings = yearlyIncome - yearlyExpense;

    // 資産計算（シミュレーションタイプに応じて分岐）
    currentAssets = simulationType === 'simple'
      ? calculateSimpleAssetReturns({ ...currentAssets }, yearlySavings)
      : calculateMonteCarloAssetReturns({ ...currentAssets }, yearlySavings, 1);

    // 結果を記録
    const totalAssets = Math.round(Object.values(currentAssets).reduce((sum, asset) => sum + asset.amount, 0));
    const totalLiabilities = Math.round(calculateTotalLiabilities(formData, age, year));
    yearlyData.push({
      age,
      income: Math.round(yearlyIncome),
      expense: Math.round(yearlyExpense),
      savings: Math.round(yearlySavings),
      assets: totalAssets,
      liabilities: totalLiabilities,
      netWorth: totalAssets - totalLiabilities,
      familyAges,
      events
    });

    // 次の年の準備
    currentIncome *= 1 + baseParams.salaryIncreaseRate;
    currentExpense *= 1 + baseParams.inflationRate;
  }

  return yearlyData;
}

// 簡易シミュレーション
export function calculateInstantSimulation(formData: z.infer<typeof lifePlanSchema>) {
  return runSimulation(formData, 'simple');
}

// モンテカルロシミュレーション
export function calculateFullSimulation(formData: z.infer<typeof lifePlanSchema>) {
  const yearlyData = runSimulation(formData, 'monteCarlo');

  // リスク分析データの計算
  const riskAnalysis = calculateRiskAnalysis(formData, yearlyData);

  // サマリーデータの計算
  const summary = calculateSummaryData(
    yearlyData,
    Object.values(formData.assets).reduce((sum, asset) => sum + asset.amount, 0),
    (formData.income.annualIncome + formData.income.annualBonus) / 12,
    Object.values(formData.expense as Record<string, number>).reduce((sum, value) => sum + value, 0)
  );

  // 改善提案データの計算
  const recommendations = calculateRecommendations(formData, yearlyData, summary);

  return {
    yearlyData,
    summary,
    riskAnalysis,
    recommendations,
  };
}

// 年次データを計算する関数
function calculateYearlyData(formData: z.infer<typeof lifePlanSchema>) {
  const simulationPeriod = formData.simulationSettings.simulationPeriod
  const currentAge = formData.basicInfo.age
  const inflationRate = formData.simulationSettings.inflationRate / 100
  const salaryIncreaseRate = formData.income.salaryIncreaseRate / 100
  const retirementAge = formData.lifeGoals.retirementAge

  // 配偶者と子どもの年齢を取得
  const hasSpouse = formData.basicInfo.familyStructure.hasSpouse
  const spouseAge = formData.basicInfo.familyStructure.spouseAge || 0
  const children = formData.basicInfo.familyStructure.children || []

  // 初期値の設定
  let currentAssets = { ...formData.assets }
  let currentLiabilities = Object.values(formData.liabilities).reduce((sum: number, loans: { name: string; amount: number; period: number; interestRate: number; }[]) =>
    sum + loans.reduce((loanSum, loan) => loanSum + (loan?.amount || 0), 0), 0)

  let currentIncome = formData.income.annualIncome + formData.income.annualBonus
  let currentExpense = Object.values(formData.expense as Record<string, number>).reduce((sum: number, value: number) => sum + value, 0) * 12
  const retirementExpense = formData.lifeGoals.retirementMonthlyExpense * 12

  // ライフイベントの準備
  const lifeEvents = prepareLifeEvents(formData)

  // シミュレーション結果を格納する配列
  const yearlyData = []

  // 各年のシミュレーション
  for (let year = 0; year < simulationPeriod; year++) {
    const age = currentAge + year

    // 家族の年齢を計算
    const familyAges = {
      spouseAge: hasSpouse ? spouseAge + year : 0,
      childrenAges: children.map((child: any) => child.age + year)
    }

    // 退職後は収入が変わる
    const yearlyIncome = age > retirementAge ? 0 : currentIncome

    // 負債支払い額を計算
    const liabilityPayments = calculateLiabilityPayments(formData, age, year)
    currentLiabilities -= liabilityPayments

    // ライフイベントの処理
    const yearEvents = lifeEvents.filter((event: any) => event.age === age)
    let eventExpenses = 0

    yearEvents.forEach((event: any) => {
      if (event.type === "housing") {
        eventExpenses += formData.lifeGoals.housingPurchase.amount
        // 住宅購入時は負債が増加
        currentLiabilities += formData.lifeGoals.housingPurchase.amount
      } else if (event.type === "education") {
        eventExpenses += event.amount
      } else if (event.type === "other") {
        eventExpenses += event.amount
      }
    })

    // 年間支出 = 支出 + 負債支払い + イベント支出
    const yearlyExpense = (age >= retirementAge ? retirementExpense : currentExpense)
      + liabilityPayments + eventExpenses

    // 年間の貯蓄（収入 - 支出）
    const yearlySavings = yearlyIncome - yearlyExpense

    // 資産の更新
    currentAssets = calculateInvestmentReturns(currentAssets)
    const totalAssets = Object.values(currentAssets).reduce((sum: number, asset: any) => sum + asset.amount, 0)

    // 純資産の計算（総資産 - 負債）
    const netWorth = totalAssets - currentLiabilities

    // 結果を配列に追加
    yearlyData.push({
      age,
      income: Math.round(yearlyIncome),
      expense: Math.round(yearlyExpense),
      savings: Math.round(yearlySavings),
      assets: Math.round(totalAssets),
      liabilities: Math.round(currentLiabilities),
      netWorth: Math.round(netWorth),
      familyAges,
      events: yearEvents
    })

    // 次の年の準備
    currentIncome *= 1 + salaryIncreaseRate
    currentExpense *= 1 + inflationRate
  }

  return yearlyData
}

// ライフイベントを準備する関数
function prepareLifeEvents(formData: z.infer<typeof lifePlanSchema>) {
  const events = [];
  const currentAge = formData.basicInfo.age;
  // 退職イベント
  events.push({
    age: formData.lifeGoals.retirementAge,
    type: "retirement",
    name: "lifePlan.result.timeline.retirement",
    amount: 0,
  })

  // 住宅購入イベント
  if (formData.lifeGoals.housingPurchase.planned) {
    events.push({
      age: formData.lifeGoals.housingPurchase.year,
      type: "housing",
      name: "lifePlan.result.timeline.housingPurchase",
      amount: formData.lifeGoals.housingPurchase.amount,
    })
  }

  // 教育資金イベント
  formData.lifeGoals.educationFunds.forEach((fund: any) => {
    formData.basicInfo.familyStructure.children.forEach((child: any) => {
      const eventAge = currentAge + fund.age - child.age;
      if (eventAge >= currentAge) {
        events.push({
          age: eventAge,
          type: "education",
          name: "lifePlan.result.timeline.education",
          amount: fund.amount,
          params: {
            childName: child.name || "lifePlan.result.timeline.child",
            fundName: fund.name || "lifePlan.result.timeline.educationFund"
          }
        });
      }
    });
  });

  // その他のゴールイベント
  formData.lifeGoals.otherGoals.forEach((goal: any) => {
    if (goal.year >= currentAge) {
      events.push({
        age: goal.year,
        type: getEventType(goal.name),
        name: goal.name,
        amount: goal.amount,
      })
    }
  })

  return events
}

// イベントタイプを判定する関数
function getEventType(eventName: string) {
  const lowerName = eventName.toLowerCase()

  if (lowerName.includes("車") || lowerName.includes("car")) return "car"
  if (lowerName.includes("旅行") || lowerName.includes("travel")) return "travel"
  if (lowerName.includes("結婚") || lowerName.includes("marriage")) return "marriage"
  if (lowerName.includes("子") || lowerName.includes("出産") || lowerName.includes("child")) return "child"

  return "other"
}

// 負債の支払いを計算する関数
function calculateLiabilityPayments(formData: z.infer<typeof lifePlanSchema>, age: number, year: number): number {
  // ローン支払い額を計算する共通関数
  const calculateLoanPayment = (loan: { amount: number; period: number; interestRate: number }) => {
    if (loan.amount <= 0 || loan.period <= 0) return 0

    if (loan.interestRate === 0) {
      // 金利が0の場合は単純に元金を期間で割る
      const monthlyPayment = loan.amount / (loan.period * 12)
      return year < loan.period ? monthlyPayment * 12 : 0
    } else {
      // 元利均等返済の計算
      const monthlyRate = loan.interestRate / 100 / 12
      const totalPayments = loan.period * 12
      const monthlyPayment =
        (loan.amount * (monthlyRate * Math.pow(1 + monthlyRate, totalPayments))) /
        (Math.pow(1 + monthlyRate, totalPayments) - 1)
      return year < loan.period ? monthlyPayment * 12 : 0
    }
  }

  // すべてのローンを配列に変換して処理
  const allLoans = [
    ...formData.liabilities.housingLoan,
    ...formData.liabilities.otherLoans
  ]

  // すべてのローン支払い額を合計
  return allLoans.reduce((total, loan) => total + calculateLoanPayment(loan), 0)
}

// サマリーデータを計算する関数
function calculateSummaryData(
  yearlyData: any[],
  initialAssets: number,
  initialMonthlyIncome: number,
  initialMonthlyExpense: number,
) {
  // 最終年齢と資産
  const finalData = yearlyData[yearlyData.length - 1]
  const finalAge = finalData.age
  const finalAssets = finalData.assets

  // ピーク時の資産と年齢
  const peakAssetsData = [...yearlyData].sort((a, b) => b.assets - a.assets)[0]
  const peakAssets = peakAssetsData.assets
  const peakAge = peakAssetsData.age

  // 退職時の資産と収支
  const retirementData = yearlyData.find((data) => data.events.some((e: any) => e.type === "retirement"))
  const retirementAge = retirementData?.age || 0
  const retirementAssets = retirementData?.assets || 0
  const retirementIncome = yearlyData.find((data) => data.age === retirementAge + 1)?.income || 0
  const retirementExpense = yearlyData.find((data) => data.age === retirementAge + 1)?.expense || 0

  // 貯蓄率
  const savingsRate =
    initialMonthlyIncome > 0 ? (initialMonthlyIncome - initialMonthlyExpense) / initialMonthlyIncome : 0

  // 資産成長率
  const assetGrowthRate = initialAssets > 0 ? (finalAssets - initialAssets) / initialAssets / yearlyData.length : 0

  // リスクレベルの判定
  const hasNegativeAssets = yearlyData.some((data) => data.netWorth < 0)
  const riskLevel = determineRiskLevel(yearlyData, savingsRate)

  return {
    totalAssets: initialAssets,
    peakAssets,
    peakAge,
    retirementAssets,
    retirementAge,
    finalAssets,
    finalAge,
    monthlyIncomeAtRetirement: retirementIncome / 12,
    monthlyExpenseAtRetirement: retirementExpense / 12,
    savingsRate,
    assetGrowthRate,
    riskLevel,
    hasNegativeAssets,
  }
}

// リスクレベルを判定する関数
function determineRiskLevel(yearlyData: any[], savingsRate: number) {
  // 資産がマイナスになる年があるか
  const hasNegativeAssets = yearlyData.some((data) => data.netWorth < 0)

  // 退職後に資産が減少し続けるか
  const retirementIndex = yearlyData.findIndex((data) => data.events.some((e: any) => e.type === "retirement"))
  let postRetirementDecline = false

  if (retirementIndex >= 0) {
    const retirementAssets = yearlyData[retirementIndex].assets
    const finalAssets = yearlyData[yearlyData.length - 1].assets
    postRetirementDecline = finalAssets < retirementAssets * 0.5
  }

  // 貯蓄率が低いか
  const lowSavingsRate = savingsRate < 0.1

  if (hasNegativeAssets || (postRetirementDecline && lowSavingsRate)) {
    return "high"
  } else if (postRetirementDecline || lowSavingsRate) {
    return "medium"
  } else {
    return "low"
  }
}

type assets = {
  savings: { amount: number, return: number, volatility: number },
  stocks: { amount: number, return: number, volatility: number },
  bonds: { amount: number, return: number, volatility: number },
  mutualFunds: { amount: number, return: number, volatility: number },
  insurance: { amount: number, return: number, volatility: number },
  other: { amount: number, return: number, volatility: number },
}
// 投資資産の運用リターンを計算する関数
export function calculateInvestmentReturns(assets: assets): assets {
  // 新しい資産オブジェクトを作成
  const newAssets = {
    savings: { ...assets.savings },
    stocks: { ...assets.stocks },
    bonds: { ...assets.bonds },
    mutualFunds: { ...assets.mutualFunds },
    insurance: { ...assets.insurance },
    other: { ...assets.other }
  };

  // 各資産のリターンを計算
  Object.entries(newAssets).forEach(([key, asset]) => {
    newAssets[key as keyof assets].amount = asset.amount * (1 + (asset.return || 0) / 100);
  });

  return newAssets;
}

// リスク分析データを計算する関数
function calculateRiskAnalysis(formData: any, yearlyData: any[]) {
  // 基本シナリオ
  const baseCase = yearlyData.map((data) => ({
    age: data.age,
    assets: data.assets,
  }));

  // 悲観シナリオ（運用利回り低下、インフレ率上昇）
  const worstCaseFormData = {
    ...formData,
    assets: {
      ...formData.assets,
      stocks: {
        ...formData.assets.stocks,
        return: Math.max(1, formData.assets.stocks.return - 2),
      },
      bonds: {
        ...formData.assets.bonds,
        return: Math.max(1, formData.assets.bonds.return - 2),
      },
      mutualFunds: {
        ...formData.assets.mutualFunds,
        return: Math.max(1, formData.assets.mutualFunds.return - 2),
      },
      insurance: {
        ...formData.assets.insurance,
        return: Math.max(1, formData.assets.insurance.return - 2),
      },
      other: {
        ...formData.assets.other,
        return: Math.max(1, formData.assets.other.return - 2),
      },
    },
    simulationSettings: {
      ...formData.simulationSettings,
      inflationRate: formData.simulationSettings.inflationRate + 1,
    },
  };
  const worstCaseData = calculateYearlyData(worstCaseFormData);
  const worstCase = worstCaseData.map((data) => ({
    age: data.age,
    assets: data.assets,
  }));

  // 楽観シナリオ（運用利回り上昇、インフレ率低下）
  const bestCaseFormData = {
    ...formData,
    assets: {
      ...formData.assets,
      stocks: {
        ...formData.assets.stocks,
        return: formData.assets.stocks.return + 2,
      },
      bonds: {
        ...formData.assets.bonds,
        return: formData.assets.bonds.return + 2,
      },
      mutualFunds: {
        ...formData.assets.mutualFunds,
        return: formData.assets.mutualFunds.return + 2,
      },
      insurance: {
        ...formData.assets.insurance,
        return: formData.assets.insurance.return + 2,
      },
      other: {
        ...formData.assets.other,
        return: formData.assets.other.return + 2,
      },
    },
    simulationSettings: {
      ...formData.simulationSettings,
      inflationRate: Math.max(0, formData.simulationSettings.inflationRate - 0.5),
    },
  };
  const bestCaseData = calculateYearlyData(bestCaseFormData);
  const bestCase = bestCaseData.map((data) => ({
    age: data.age,
    assets: data.assets,
  }));

  // インフレリスク
  const inflationRiskFormData = {
    ...formData,
    simulationSettings: {
      ...formData.simulationSettings,
      inflationRate: formData.simulationSettings.inflationRate + 2,
    },
  };
  const inflationRiskData = calculateYearlyData(inflationRiskFormData);
  const inflationRisk = inflationRiskData.map((data) => ({
    age: data.age,
    assets: data.assets,
  }));

  // 投資リスク（各資産のボラティリティを考慮）
  const investmentRiskFormData = {
    ...formData,
    assets: {
      ...formData.assets,
      stocks: {
        ...formData.assets.stocks,
        return: Math.max(0, formData.assets.stocks.return - formData.assets.stocks.volatility / 2),
      },
      bonds: {
        ...formData.assets.bonds,
        return: Math.max(0, formData.assets.bonds.return - formData.assets.bonds.volatility / 2),
      },
      mutualFunds: {
        ...formData.assets.mutualFunds,
        return: Math.max(0, formData.assets.mutualFunds.return - formData.assets.mutualFunds.volatility / 2),
      },
      insurance: {
        ...formData.assets.insurance,
        return: Math.max(0, formData.assets.insurance.return - formData.assets.insurance.volatility / 2),
      },
      other: {
        ...formData.assets.other,
        return: Math.max(0, formData.assets.other.return - formData.assets.other.volatility / 2),
      },
    },
  };
  const investmentRiskData = calculateYearlyData(investmentRiskFormData);
  const investmentRisk = investmentRiskData.map((data) => ({
    age: data.age,
    assets: data.assets,
  }));

  // 長寿リスク
  const longevityRiskFormData = {
    ...formData,
    simulationSettings: {
      ...formData.simulationSettings,
      simulationPeriod: formData.simulationSettings.simulationPeriod + 10,
    },
  };
  const longevityRiskData = calculateYearlyData(longevityRiskFormData);
  const longevityRisk = longevityRiskData.map((data) => ({
    age: data.age,
    assets: data.assets,
  }));

  // リスク要因の分析
  const riskFactors = [
    {
      name: "インフレリスク",
      description: "物価上昇率が想定より高くなると、実質的な資産価値や購買力が低下します。",
      impact: determineRiskImpact(baseCase, inflationRisk),
      recommendation: "インフレに強い資産（株式、REIT、物価連動債など）への分散投資を検討してください。",
    },
    {
      name: "投資リターンリスク",
      description: "運用利回りが想定を下回ると、資産形成が計画通りに進まない可能性があります。",
      impact: determineRiskImpact(baseCase, investmentRisk),
      recommendation: "長期的な視点で分散投資を行い、定期的なリバランスを実施してください。",
    },
    {
      name: "長寿リスク",
      description: "予想以上に長生きすると、退職後の資金が不足する可能性があります。",
      impact: determineRiskImpact(baseCase, longevityRisk),
      recommendation: "退職後も一定の収入源を確保するか、余裕を持った資産形成を心がけてください。",
    },
    {
      name: "収入減少リスク",
      description: "失業や収入減少により、計画通りの貯蓄ができなくなる可能性があります。",
      impact: formData.income.salaryIncreaseRate > 1 ? "medium" : "high",
      recommendation: "緊急資金として3〜6ヶ月分の生活費を流動性の高い資産で確保してください。",
    },
  ];

  // 退職年齢の取得
  const retirementAge = formData.lifeGoals.retirementAge;

  return {
    baseCase,
    worstCase,
    bestCase,
    inflationRisk,
    investmentRisk,
    longevityRisk,
    retirementAge,
    riskFactors,
  }
}

// リスクの影響度を判定する関数
function determineRiskImpact(baseCase: any[], riskCase: any[]) {
  // 最終資産の差を計算
  const baseFinaAssets = baseCase[baseCase.length - 1].assets
  const riskFinalAssets = riskCase[riskCase.length - 1].assets

  const assetReduction = (baseFinaAssets - riskFinalAssets) / baseFinaAssets

  if (assetReduction > 0.5 || riskFinalAssets <= 0) {
    return "high"
  } else if (assetReduction > 0.2) {
    return "medium"
  } else {
    return "low"
  }
}

// 改善提案データを計算する関数
function calculateRecommendations(formData: z.infer<typeof lifePlanSchema>, yearlyData: any[], summary: any) {
  // 現在の貯蓄率
  const savingsRate = summary.savingsRate

  // 目標貯蓄率（収入の20%が理想的）
  const targetSavingsRate = 0.2

  // 現在の投資配分
  const totalAssets = summary.totalAssets
  const investmentAssets = Object.values(formData.assets).reduce((sum, asset) => sum + asset.amount, 0)

  const investmentRatio = totalAssets > 0 ? investmentAssets / totalAssets : 0

  // 目標投資配分（年齢に応じた配分）
  const age = formData.basicInfo.age
  const targetInvestmentRatio = Math.max(0.3, 1 - age / 100)

  // 現在の支出率
  const expenseRate = 1 - savingsRate

  // 目標支出率
  const targetExpenseRate = 1 - targetSavingsRate

  // 退職時の資産と必要資産
  const retirementAge = formData.lifeGoals.retirementAge
  const lifeExpectancy = 95
  const yearsInRetirement = lifeExpectancy - retirementAge
  const monthlyExpenseAtRetirement = formData.lifeGoals.retirementMonthlyExpense
  const annualExpenseAtRetirement = monthlyExpenseAtRetirement * 12

  // 必要な退職資金（単純計算）
  const targetRetirementAssets = annualExpenseAtRetirement * yearsInRetirement

  // 退職時の予測資産
  const retirementAssets = summary.retirementAssets

  // アクションアイテム
  const actionItems = []

  // 貯蓄率が低い場合
  if (savingsRate < targetSavingsRate) {
    actionItems.push({
      title: "貯蓄率の向上",
      description: `現在の貯蓄率は${(savingsRate * 100).toFixed(1)}%です。収入の20%以上を貯蓄することを目指しましょう。`,
      priority: savingsRate < 0.1 ? "high" : "medium",
      completed: savingsRate >= targetSavingsRate,
    })
  }

  // 投資配分が低い場合
  if (investmentRatio < targetInvestmentRatio) {
    actionItems.push({
      title: "投資配分の見直し",
      description: `現在の投資配分は資産全体の${(investmentRatio * 100).toFixed(1)}%です。${(targetInvestmentRatio * 100).toFixed(1)}%程度まで増やすことを検討しましょう。`,
      priority: investmentRatio < 0.1 ? "high" : "medium",
      completed: investmentRatio >= targetInvestmentRatio,
    })
  }

  // 退職資金が不足する場合
  if (retirementAssets < targetRetirementAssets) {
    actionItems.push({
      title: "退職資金の積み増し",
      description: `現在の計画では退職時に${retirementAssets.toLocaleString()}万円の資産となる見込みですが、理想的には${targetRetirementAssets.toLocaleString()}万円程度必要です。`,
      priority: retirementAssets < targetRetirementAssets * 0.7 ? "high" : "medium",
      completed: retirementAssets >= targetRetirementAssets,
    })
  }

  // 資産がマイナスになる期間がある場合
  if (summary.hasNegativeAssets) {
    actionItems.push({
      title: "収支バランスの改善",
      description:
        "シミュレーションでは資産がマイナスになる期間があります。支出の見直しや収入の増加を検討してください。",
      priority: "high",
      completed: false,
    })
  }

  // 緊急資金が不足している場合
  const emergencyFund = formData.assets.savings.amount
  const monthlyExpense = Object.values(formData.expense as Record<string, number>).reduce((sum: number, value: number) => sum + value, 0)
  const recommendedEmergencyFund = monthlyExpense * 6

  if (emergencyFund < recommendedEmergencyFund) {
    actionItems.push({
      title: "緊急資金の確保",
      description: `緊急時に備えて、最低でも${Math.round(recommendedEmergencyFund).toLocaleString()}万円（生活費の6ヶ月分）を流動性の高い資産で確保しましょう。`,
      priority: emergencyFund < monthlyExpense * 3 ? "high" : "medium",
      completed: emergencyFund >= recommendedEmergencyFund,
    })
  }

  return {
    savingsRecommendation: {
      current: savingsRate,
      target: targetSavingsRate,
      description: `収入の${(targetSavingsRate * 100).toFixed(0)}%以上を貯蓄に回すことで、将来の資産形成が安定します。`,
    },
    investmentRecommendation: {
      current: investmentRatio,
      target: targetInvestmentRatio,
      description: `資産の${(targetInvestmentRatio * 100).toFixed(0)}%程度を成長性のある投資に配分することで、長期的な資産形成が促進されます。`,
    },
    expenseRecommendation: {
      current: expenseRate,
      target: targetExpenseRate,
      description: `支出を収入の${(targetExpenseRate * 100).toFixed(0)}%以下に抑えることで、貯蓄率を高めることができます。`,
    },
    retirementRecommendation: {
      current: retirementAssets,
      target: targetRetirementAssets,
      description: `退職後${yearsInRetirement}年間の生活を維持するためには、約${targetRetirementAssets.toLocaleString()}万円の資産が必要です。`,
    },
    actionItems,
  }
}

// 簡易シミュレーション用の資産計算
function calculateSimpleAssetReturns(
  assets: assets,
  yearlySavings: number
): assets {
  // 新しい資産オブジェクトを作成
  const newAssets = {
    savings: { ...assets.savings },
    stocks: { ...assets.stocks },
    bonds: { ...assets.bonds },
    mutualFunds: { ...assets.mutualFunds },
    insurance: { ...assets.insurance },
    other: { ...assets.other }
  };

  // 貯蓄を加算
  newAssets.savings.amount += yearlySavings;

  // 単純なリターン計算
  return calculateInvestmentReturns(newAssets);
}

// モンテカルロシミュレーション用の資産計算
function calculateMonteCarloAssetReturns(
  assets: assets,
  yearlySavings: number,
  years: number
): assets {
  // 新しい資産オブジェクトを作成
  const newAssets = {
    savings: { ...assets.savings },
    stocks: { ...assets.stocks },
    bonds: { ...assets.bonds },
    mutualFunds: { ...assets.mutualFunds },
    insurance: { ...assets.insurance },
    other: { ...assets.other }
  };

  // 貯蓄を加算
  newAssets.savings.amount += yearlySavings;

  // モンテカルロシミュレーションを実行
  const simulationResult = runMultiAssetSimulation(
    newAssets,
    years,
    1 // 1回のシミュレーション
  );

  // シミュレーション結果を資産に反映
  Object.entries(simulationResult.results[0].assetValues).forEach(([key, value]) => {
    newAssets[key as keyof assets].amount = value;
  });

  return newAssets;
}
